import {
  AIExtractedFieldCard,
  downloadDocumentFile,
  openBinaryPdfInWindow,
} from "@platform/btx-grid-components";
import { Box, Link, TableCell, TableRow } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FC, useCallback, useState } from "react";
import { Typography } from "@platform/design-system";

import type { AxiosInstance } from "axios";
import type { AiRateFieldMeta, AiRateFieldsMeta } from "../../../types/models";

const CORNER_SIZE = 9;

const HEADER_LABELS = [
  "Class Name",
  "Field Name",
  "Value",
  "Page No.",
  "Justification",
];

const PAGE_LINK_SX = {
  fontSize: 15,
  fontWeight: "fontWeightRegular",
  color: "brandSupporting.main",
  cursor: "pointer",
} as const;

export interface AiRateIndicatorProps {
  /** Combined AI extraction metadata for this plan's rate-area fields */
  aiFields?: AiRateFieldsMeta;
  /** Axios instance for downloading documents */
  axiosInstance?: AxiosInstance;
}

/** Renders a single popover row for an AI-extracted field. */
const AiFieldRow: FC<{
  className?: string;
  label: string;
  fieldData: AiRateFieldMeta;
  axiosInstance?: AxiosInstance;
  isLast: boolean;
}> = ({ className, label, fieldData, axiosInstance, isLast }) => {
  const handleViewDocument = useCallback((): void => {
    const downloadLink = fieldData.documentDownloadUrl;
    if (!downloadLink || !axiosInstance) return;

    const isPdf = downloadLink.toLowerCase().endsWith(".pdf");
    if (!isPdf) return;

    const viewerWindow = window.open("", "_blank");
    if (!viewerWindow) return;
    viewerWindow.opener = null;

    void (async () => {
      try {
        if (
          !openBinaryPdfInWindow(
            viewerWindow,
            await downloadDocumentFile(downloadLink, axiosInstance),
            fieldData.pageNumber ?? undefined,
          )
        ) {
          viewerWindow.close();
        }
      } catch {
        viewerWindow.close();
      }
    })();
  }, [fieldData.documentDownloadUrl, fieldData.pageNumber, axiosInstance]);

  const borderBottom = isLast ? "none" : undefined;

  return (
    <TableRow>
      <TableCell sx={{ pt: 2, borderBottom }}>
        <Typography variant="body-medium">{className || "-"}</Typography>
      </TableCell>
      <TableCell sx={{ pt: 2, borderBottom }}>
        <Typography variant="body-medium">{label}</Typography>
      </TableCell>
      <TableCell sx={{ pt: 2, borderBottom }}>
        <Typography variant="body-medium">{fieldData.value || "-"}</Typography>
      </TableCell>
      <TableCell sx={{ pt: 2, borderBottom }}>
        {fieldData.documentDownloadUrl && fieldData.pageNumber != null ? (
          <Link
            component="button"
            type="button"
            underline="always"
            onClick={handleViewDocument}
            sx={PAGE_LINK_SX}
          >
            {`Page ${fieldData.pageNumber}`}
          </Link>
        ) : (
          <Typography variant="body-medium">
            {fieldData.pageNumber != null
              ? `Page ${fieldData.pageNumber}`
              : "-"}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ pt: 2, borderBottom }}>
        <Typography variant="body-medium">
          {fieldData.justification || "-"}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

/**
 * Renders a blue corner triangle on rate cells that have AI extraction data,
 * with a hover popover showing extraction details (value, page, justification).
 *
 * Supports multiple fields (Rates and/or Enrolled Lives), each rendered as a
 * separate row in the popover.
 *
 * Uses the shared AIExtractedFieldCard from btx-grid-components with a custom
 * 5-column layout that includes Class Name.
 */
export const AiRateIndicator: FC<AiRateIndicatorProps> = ({
  aiFields,
  axiosInstance,
}) => {
  const theme = useTheme();
  const extractedColor = (
    theme.palette as unknown as Record<string, Record<string, string>>
  )["ai"]?.["extracted"];
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isOverTrigger, setIsOverTrigger] = useState(false);
  const [isOverCard, setIsOverCard] = useState(false);

  if (!aiFields?.rate && !aiFields?.enrolledLives) return null;

  const open = isOverTrigger || isOverCard;

  const handleTriggerEnter = (e: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(e.currentTarget);
    setIsOverTrigger(true);
  };
  const handleTriggerLeave = (): void => setIsOverTrigger(false);

  // Use whichever field is available for the shared card's required row prop.
  const primaryField = aiFields.rate ?? aiFields.enrolledLives!;
  const row = {
    className: primaryField.planName ?? "",
    fieldName: aiFields.rate ? "Rates" : "Enrolled Lives",
    value: primaryField.value || "",
    pdfPage:
      primaryField.pageNumber != null ? `Page ${primaryField.pageNumber}` : "-",
    pdfPageNumber: primaryField.pageNumber ?? 0,
    justification: primaryField.justification || "",
    documentDownloadUrl: primaryField.documentDownloadUrl ?? undefined,
    documentName: primaryField.documentName ?? undefined,
  };

  // Build the list of rows to render.
  const rows: { className?: string; label: string; data: AiRateFieldMeta }[] =
    [];
  if (aiFields.enrolledLives) {
    rows.push({
      className: aiFields.enrolledLives.planName,
      label: "Enrolled Lives",
      data: aiFields.enrolledLives,
    });
  }
  if (aiFields.rate) {
    rows.push({
      className: aiFields.rate.planName,
      label: "Rates",
      data: aiFields.rate,
    });
  }

  return (
    <>
      {/* Blue corner triangle */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          borderStyle: "solid",
          borderWidth: `${CORNER_SIZE}px ${CORNER_SIZE}px 0 0`,
          borderColor: `${extractedColor ?? "#1976d2"} transparent transparent transparent`,
          zIndex: 2,
        }}
      />
      {/* Invisible hover target over the corner */}
      <Box
        onMouseEnter={handleTriggerEnter}
        onMouseLeave={handleTriggerLeave}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: CORNER_SIZE,
          height: CORNER_SIZE,
          cursor: "pointer",
          zIndex: 3,
        }}
      />
      {/* Popover using shared AIExtractedFieldCard */}
      <AIExtractedFieldCard
        headerLabels={HEADER_LABELS}
        row={row}
        anchorEl={anchorEl}
        open={open}
        onMouseEnter={() => setIsOverCard(true)}
        onMouseLeave={() => setIsOverCard(false)}
        axiosInstance={axiosInstance}
        width="60vw"
      >
        {rows.map(({ className, label, data }, idx) => (
          <AiFieldRow
            key={label}
            className={className}
            label={label}
            fieldData={data}
            axiosInstance={axiosInstance}
            isLast={idx === rows.length - 1}
          />
        ))}
      </AIExtractedFieldCard>
    </>
  );
};
